import React from 'react';

function PathwaysPanel({ response, inputs }) {
  // Always show PathwaysPanel when response data is available
  if (!response?.scores) {
    return null;
  }

  const currentTransplants = Math.round(response.scores.centerTransplants);
  const maxAchievementTarget = Math.round(response.scores.transplantTarget * 1.25); // Target for max achievement score
  
  // If slider is at or below baseline, show the max achievement target
  // Otherwise show the user's projected target
  const projectedTransplants = inputs.numTransplants > currentTransplants 
    ? inputs.numTransplants 
    : maxAchievementTarget;
  
  const additionalAchieved = projectedTransplants - currentTransplants;

  return (
    <section style={{ marginTop: '20px' }} aria-label="Pathways to Achieve Target">
      {/* Header */}
      <div style={{
        marginBottom: '24px'
      }}>
        <h2 style={{ 
          fontSize: '28px', 
          fontWeight: '700', 
          color: '#0e4f56', 
          margin: '0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          Maximize your IOTA Earnings
        </h2>
      </div>
      
      {/* Two Column Layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '40px',
        alignItems: 'start'
      }}>
        {/* Left Side: Description and Button */}
        <div>
          {/* Project Donor Impact Section */}
          <div style={{
            marginBottom: '24px'
          }}>
            <p style={{
              fontSize: '16px',
              lineHeight: '1.6',
              color: 'rgba(0,0,0,0.75)',
              margin: '0'
            }}>
              Your IOTA payment depends on completing more transplants. Increasing your transplant volume is made easy with <a href="https://www.projectdonor.org" target="_blank" rel="noopener noreferrer" style={{ color: '#cd7029', fontWeight: '600', textDecoration: 'none', borderBottom: '2px solid rgba(205,112,41,0.3)', transition: 'border-color 0.2s' }} onMouseOver={(e) => e.currentTarget.style.borderBottomColor = '#cd7029'} onMouseOut={(e) => e.currentTarget.style.borderBottomColor = 'rgba(205,112,41,0.3)'}>Project Donor</a>. We work with candidate donors who were excluded for addressable issues (i.e. high BMI) and support them in meeting donation criteria.
            </p>
          </div>
          
          {/* Enhanced Call-to-Action Button */}
          <div style={{
            marginBottom: '0'
          }}>
            <div style={{
              background: '#cd7029',
              borderRadius: '12px',
              padding: '3px',
              boxShadow: '0 2px 20px #cd7029',
              display: 'inline-block'
            }}>
              <a 
                href="https://www.projectdonor.org/transplant-centers" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  background: '#cd7029',
                  color: '#ffffff',
                  padding: '14px 24px',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  fontSize: '16px',
                  fontWeight: '700',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#cd7029';
                  e.currentTarget.style.color = '#ffffff';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = '#cd7029';
                  e.currentTarget.style.color = '#ffffff';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <span>Join Project Donor</span>
                <span style={{ fontSize: '18px' }}>→</span>
              </a>
            </div>
          </div>
        </div>

        {/* Right Side: Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px'
        }}>
          {/* Current Stats */}
          <div style={{
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: '13px', 
              color: 'rgba(0,0,0,0.6)',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '8px'
            }}>
              Current
            </div>
            <div style={{ 
              fontSize: '32px', 
              fontWeight: '700',
              color: '#0e4f56',
              marginBottom: '4px'
            }}>
              {currentTransplants}
            </div>
            <div style={{ fontSize: '13px', color: 'rgba(0,0,0,0.6)' }}>
              transplants
            </div>
          </div>

          {/* Target Stats */}
          <div style={{
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: '13px', 
              color: 'rgba(0,0,0,0.6)',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '8px'
            }}>
              Target
            </div>
            <div style={{ 
              fontSize: '32px', 
              fontWeight: '700',
              color: '#cd7029',
              marginBottom: '4px'
            }}>
              {projectedTransplants}
            </div>
            <div style={{ fontSize: '13px', color: 'rgba(0,0,0,0.6)' }}>
              transplants
            </div>
          </div>

          {/* Additional Needed */}
          <div style={{
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: '13px', 
              color: 'rgba(0,0,0,0.6)',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '8px'
            }}>
              Additional Needed
            </div>
            <div style={{ 
              fontSize: '32px', 
              fontWeight: '700',
              color: '#0e4f56',
              marginBottom: '4px'
            }}>
              +{additionalAchieved}
            </div>
            <div style={{ fontSize: '13px', color: 'rgba(0,0,0,0.6)' }}>
              ({Math.round((additionalAchieved / response.scores.centerTransplants) * 100)}% increase)
            </div>
          </div>
        </div>
      </div>

      {/* Add shimmer animation */}
      <style>
        {`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}
      </style>
    </section>
  );
}

export default PathwaysPanel;
